/*
 * Author: LJH
 * Date: 2021/12/23
 * Description: bespoke 电话预约
 */
import React, { useState, useRef } from 'react';
import { useMount, useWindowSize } from 'react-use';
import SEO from '../../../components/SEO';
import Layout from '../../../components/Layout';
import Select from '../../../components/Select';
import MyInput from '../../../components/Input';
import Popup from '../../../components/Popup/Popup';
import Popover from '../../../components/Popover';
import Judge from '../../../components/Judge';
import Dialog from '../../../components/Dialog/index.js';
import { Desktop, Tablet } from '../../../constants/responsive';
import { css } from '@emotion/core';
import { FormattedHTMLMessage, useIntl } from 'gatsby-plugin-intl';
import Toast from '../../../components/Toast';

import workHours from '../../../config/workHours';
import monthMap from '../../../config/month';
import envs from '../../../constants/environment';

import logo from '../../../images/logo/bespoke.png';

import { bespokeMainColor } from '../../../constants/colors';
import '../../../styles/bespoke/appointment.scss';
import ajax from '../../../utils/ajax';
import mpaasTracker from '../../../utils/mpaasTracker';
import {
  to,
  validate,
  testMainlandPhone,
  testHongKongPhone,
  testEmail,
} from '../../../utils/utils';

let isAppointment = false;
let formObj = {
  origin: 2,
  operateType: 1000,
  mobileArea: '+852',
};
const mobileArea = ['+852', '+86'];
// 表单验证配置
const rule_int = {
  validator: (rule, value, cb) => {
    if (value.indexOf('.') > -1) cb(rule.message);
    cb();
  },
  message: 'v5.appointment.asset_age_int_error',
};
const rules = {
  contactTime: [{ required: true, message: 'v5.appointment.required_tips' }],
  contactName: [
    { required: true, message: 'v5.appointment.required_tips' },
    {
      validator: (rule, value, cb) => {
        if (!/[a-zA-Z\s]+/g.test(value)) cb(rule.message);
        cb();
      },
      message: 'v5.appointment.name_error',
    },
  ],
  age: [
    { required: true, message: 'v5.appointment.required_tips' },
    {
      validator: (rule, value, cb) => {
        const val = Number(value);
        if (val < 18 || val > 80) cb(rule.message);
        cb();
      },
      message: 'v5.appointment.age_group_error',
    },
    rule_int,
  ],
  mobileNum: [
    { required: true, message: 'v5.appointment.required_tips' },
    {
      validator: (rule, value, cb) => {
        if (formObj.mobileArea == '+86' && !testMainlandPhone(value))
          cb(rule.message);
        if (formObj.mobileArea == '+852' && !testHongKongPhone(value))
          cb(rule.message);
        cb();
      },
      message: 'v5.appointment.phone_format_error',
    },
  ],
  contactEmail: [
    { required: true, message: 'v5.appointment.required_tips' },
    {
      validator: (rule, value, cb) => {
        if (!testEmail(value)) cb(rule.message);
        cb();
      },
      message: 'v5.appointment.email_format_error',
    },
  ],
  totalAssets: [
    { required: true, message: 'v5.appointment.required_tips' },
    {
      validator: (rule, value, cb) => {
        const val = Number(value);
        if (val < 10 ** 5 * 5 || val > 10 ** 8) cb(rule.message);
        cb();
      },
      message: 'v5.appointment.total_assets_error',
    },
    rule_int,
  ],
};

function queryWorkday(intl) {
  if (queryWorkday.cache) {
    return queryWorkday.cache;
  }
  const url = '/api/v4/extend-service/calendar/witness/available';
  queryWorkday.cache = ajax({
    method: 'get',
    url,
    params: { day: 30 },
    intl,
  });
  queryWorkday.cache.then((data) => (queryWorkday.cache = data));
  return queryWorkday.cache;
}

async function submit(intl) {
  let url = '/api/v4/users/bespoke/appointment';
  // 更新
  if (isAppointment && localStorage.bespoke_appointment) {
    url = '/api/v4/users/bespoke/appointment/update';
  }
  const _formObj = { ...formObj };
  delete _formObj.contactTime;
  const [err, result] = await to(
    ajax({
      method: 'post',
      url,
      data: _formObj,
      intl,
    })
  );
  if (err) return Promise.reject(err);
  localStorage.bespoke_appointment = JSON.stringify(formObj);
  isAppointment = false;
  // 滚动到顶部
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, 0);
  return result;
}

// 取消预约
async function cancel(
  intl,
  { title, content, confirmBtnText, cancelBtnText },
  isPC
) {
  const className = 'cancel-appointment-confirm';
  if (isPC) {
    const [, cancel] = await to(
      Dialog.confirm({
        title,
        content,
        confirmBtnText,
        cancelBtnText,
        className,
      })
    );
    if (cancel) return Promise.reject('cancel');
  }
  const url = '/api/v4/users/bespoke/appointment/update';
  const _formObj = { ...formObj };
  _formObj.operateType = 2000;
  delete _formObj.contactTime;
  const [err] = await to(
    ajax({
      method: 'post',
      url,
      data: _formObj,
      intl,
    })
  );
  if (err) return Promise.reject(err);
  Toast(intl.formatMessage({ id: 'v5.appointment.cancel_success_text' }));
  localStorage.removeItem('bespoke_appointment');
  isAppointment = false;
  return Promise.resolve(true);
}
// 劫持返回按键
function abductionBack(onSuccess) {
  window.history.pushState('abductionBack', '', location.href);
  window.onpopstate = (e) => {
    onSuccess();
  };
}

const Form = (props) => {
  const intl = useIntl();
  const isPC = props.isPC;
  const wdRef = useRef(null);
  const [contactTime, setContactTime] = useState('');
  const [workdays, setWorkdays] = useState([]);
  const [toggleWorkdaySelect, setToggleWorkdaySelect] = useState(false);
  const [formError, setFormError] = useState({});
  useMount(async () => {
    const [err, data] = await to(queryWorkday(intl));
    if (err) {
      console.log(err);
      return false;
    }
    setWorkdays(data || []);
  });
  function setTime(result) {
    if (!result) result = wdRef.current.getValue();
    setContactTime(result.join('  '));
    const [date, time] = result;
    const times = time.split('-');
    // 辅助字段检测用
    formObj.contactTime = result;
    formObj.contactStartTime = `${date} ${times[0]}:00`;
    formObj.contactEndTime = `${date} ${times[1]}:00`;
  }
  function setFormObj(key, value) {
    formObj[key] = value;
  }
  function doValidate(key, value) {
    validate(rules[key], value, key, (error, inputF) => {
      let message = null;
      if (error && error[0]) {
        message = intl.formatMessage({ id: error[0].message });
      }
      formError[key] = message;
      setFormError({ ...formError });
      console.log(formError);
    });
  }
  function _setToggleWorkdaySelect(bool) {
    setToggleWorkdaySelect(bool);
    doValidate('contactTime', formObj.contactTime);
  }
  function doSubmit() {
    mpaasTracker.Click('Bespoke_BookPage_ConfirmAppointment', {
      bizType: 'Button',
      ext: { uid: localStorage.getItem('uid') },
    });
    for (let key in rules) {
      doValidate(key, formObj[key]);
    }
    // 部分字段有误
    if (Object.values(formError).some((it) => it)) {
      return false;
    }

    submit(intl, formObj).then(() => props.onSuccess(true));
  }
  return (
    <>
      <section css={csss.main}>
        <div
          css={css`
            ${csss.title};
            ${csss.mainPadding}
          `}
        >
          <FormattedHTMLMessage
            tag='b'
            id='v5.appointment.appointment_title'
          ></FormattedHTMLMessage>
        </div>
        <div
          css={css`
            ${csss.explain};
            ${csss.mainPadding}
          `}
        >
          <FormattedHTMLMessage id='v5.appointment.appointment_explain'></FormattedHTMLMessage>
        </div>
        <div css={csss.inputPadding}>
          <MyInput
            value={contactTime}
            css={css`
              ${csss.select}
            `}
            className='select'
            popover='wd_select'
            inputStyle={{ borderRight: 'none' }}
            inputCss={csss.inputFontSize}
            readOnly='readonly'
            placeholder={intl.formatMessage({
              id: 'v5.appointment.select_date_placeholder',
            })}
            onClick={() => setToggleWorkdaySelect(!toggleWorkdaySelect)}
          ></MyInput>
          <p css={csss.error_text} className='color-error'>
            {formError.contactTime}
          </p>
        </div>
        <div className='text-center' css={csss.sub_title}>
          <FormattedHTMLMessage id='v5.appointment.baseInfo_title'></FormattedHTMLMessage>
        </div>
        <div css={csss.inputPadding}>
          <MyInput
            defaultValue={formObj.contactName}
            placeholder={intl.formatMessage({
              id: 'v5.appointment.your_name_text',
            })}
            inputCss={csss.inputFontSize}
            onInput={(e) => setFormObj('contactName', e.target.value)}
            onBlur={(e) => doValidate('contactName', e.target.value)}
            onChange={(e) =>
              formError.contactName && doValidate('contactName', e.target.value)
            }
          ></MyInput>
          <p css={csss.error_text} className='color-error'>
            {formError.contactName}
          </p>
        </div>
        <div css={csss.inputPadding}>
          <MyInput
            defaultValue={formObj.age}
            type='number'
            inputClass='spin-btn-hide'
            placeholder={intl.formatMessage({
              id: 'v5.appointment.your_age_text',
            })}
            inputCss={csss.inputFontSize}
            onInput={(e) => setFormObj('age', e.target.value)}
            onBlur={(e) => doValidate('age', e.target.value)}
            onChange={(e) => formError.age && doValidate('age', e.target.value)}
          ></MyInput>
          <p css={csss.error_text} className='color-error'>
            {formError.age}
          </p>
        </div>
        <div css={csss.inputPadding}>
          <MyInput
            defaultValue={formObj.mobileNum}
            type='tel'
            placeholder={intl.formatMessage({
              id: 'v5.appointment.phone_number_text',
            })}
            inputCss={csss.inputFontSize}
            onInput={(e) => setFormObj('mobileNum', e.target.value)}
            onBlur={(e) => doValidate('mobileNum', e.target.value)}
            onChange={(e) =>
              formError.mobileNum && doValidate('mobileNum', e.target.value)
            }
          >
            <select
              defaultValue={formObj.mobileArea}
              css={css`
                ${csss.select};
                ${csss.inputFontSize}
              `}
              className='select'
              key='prefix'
              onInput={(e) => setFormObj('mobileArea', e.target.value)}
              onChange={() => {
                !!formObj.mobileNum &&
                  doValidate('mobileNum', formObj.mobileNum);
              }}
            >
              {mobileArea.map((it) => (
                <option value={it} key={it}>
                  {it}
                </option>
              ))}
            </select>
          </MyInput>
          <p css={csss.error_text} className='color-error'>
            {formError.mobileNum}
          </p>
        </div>
        {/*邮箱*/}
        <div css={csss.inputPadding}>
          <MyInput
            defaultValue={formObj.contactEmail}
            placeholder={intl.formatMessage({
              id: 'v5.appointment.your_email_text',
            })}
            inputCss={csss.inputFontSize}
            onInput={(e) => setFormObj('contactEmail', e.target.value)}
            onBlur={(e) => doValidate('contactEmail', e.target.value)}
            onChange={(e) =>
              formError.contactEmail &&
              doValidate('contactEmail', e.target.value)
            }
          ></MyInput>
          <p css={csss.error_text} className='color-error'>
            {formError.contactEmail}
          </p>
        </div>
        <div css={csss.inputPadding}>
          <MyInput
            defaultValue={formObj.totalAssets}
            type='number'
            inputClass='spin-btn-hide'
            inputCss={csss.inputFontSize}
            placeholder={intl.formatMessage({
              id: 'v5.appointment.your_total_assets_text',
            })}
            onInput={(e) => setFormObj('totalAssets', e.target.value)}
            onBlur={(e) => doValidate('totalAssets', e.target.value)}
            onChange={(e) =>
              formError.totalAssets && doValidate('totalAssets', e.target.value)
            }
          >
            <span
              key='suffix'
              css={css`
                ${csss.currency};
                ${csss.inputFontSize}
              `}
            >
              HKD
            </span>
          </MyInput>
          <p css={csss.error_text}>
            {formError.totalAssets ? (
              <span className='color-error'>{formError.totalAssets}</span>
            ) : (
              <FormattedHTMLMessage id='v5.appointment.Disclaimer_of_assets'></FormattedHTMLMessage>
            )}
          </p>
        </div>
        <button
          onClick={doSubmit}
          css={css`
            ${csss.btnPrimaryF} ${csss.submitBtn}
          `}
          className='btn'
          disabled={
            Object.values(formError).length < 1 ||
            Object.values(formError).some((it) => it !== null)
          }
        >
          {intl.formatMessage({
            id: 'v5.appointment.Confirm_Appointment_text',
          })}
        </button>
      </section>
      <Judge>
        {/*PC时间选择悬浮框*/}
        <Popover
          if={isPC}
          show={toggleWorkdaySelect}
          actionDom="[popover='wd_select']"
          changeShow={() => _setToggleWorkdaySelect(false)}
        >
          <Select
            columns={[0, 0]}
            data={[workdays, workHours]}
            ref={wdRef}
            style={{ width: '300px' }}
            onChange={(e) => setTime(e)}
          />
        </Popover>
        {/*移动时间选择弹窗*/}
        <Popup
          else
          show={toggleWorkdaySelect}
          changeShow={(e) => _setToggleWorkdaySelect(false)}
        >
          <div>
            <button
              className='btn btn-text'
              onClick={() => _setToggleWorkdaySelect(false)}
            >
              <FormattedHTMLMessage id='v5.appointment.btn_cancel'></FormattedHTMLMessage>
            </button>
            <button
              className='btn btn-text btn-primary-text float-right'
              onClick={() => {
                setTime();
                _setToggleWorkdaySelect(false);
              }}
            >
              <FormattedHTMLMessage id='v5.appointment.btn_comfirn'></FormattedHTMLMessage>
            </button>
          </div>
          <Select columns={[0, 0]} data={[workdays, workHours]} ref={wdRef} />
        </Popup>
      </Judge>
    </>
  );
};

const Success = (props) => {
  const isPC = props.isPC;
  const intl = useIntl();
  const [startTime, setStartTime] = useState({});
  const confirmConfig = {
    content: intl.formatMessage({
      id: 'v5.appointment.cancel_appointment_tips',
    }),
    cancelBtnText: intl.formatMessage({ id: 'v5.appointment.yes_cancel_text' }),
    confirmBtnText: intl.formatMessage({
      id: 'v5.appointment.dont_cancel_text',
    }),
  };
  const [showPopup, setShowPopup] = useState(false);
  useMount(() => {
    const startTimeStr =
      (formObj.contactStartTime || '').replace(/-/g, '/') || null;
    const startTime = new Date(startTimeStr);
    const sTimeObj = {
      time: startTimeStr,
      year: startTime.getFullYear(),
      month: ('0' + (startTime.getMonth() + 1)).substr(-2, 2),
      emonth: intl.formatMessage({ id: monthMap[startTime.getMonth() + 1] }),
      day: ('0' + startTime.getDate()).substr(-2, 2),
      hour: (
        '0' +
        (startTime.getHours() > 12
          ? startTime.getHours() - 12
          : startTime.getHours())
      ).substr(-2, 2),
      minute: ('0' + startTime.getMinutes()).substr(-2, 2),
      apm: startTime.getHours() > 12 ? 'PM' : 'AM',
    };
    setStartTime(sTimeObj);
  });
  return (
    <>
      <section className='text-center'>
        <p>
          <Judge>
            {/*成功预约提示*/}
            <FormattedHTMLMessage
              if={!isAppointment}
              id='v5.appointment.appointment_success_tips'
            ></FormattedHTMLMessage>
            {/*已经预约提示*/}
            <FormattedHTMLMessage
              else
              id='v5.appointment.appointment_before_tips'
            ></FormattedHTMLMessage>
          </Judge>
        </p>
        <div
          className='appointment-time'
          style={{ opacity: startTime.time ? 1 : 0 }}
        >
          {intl.formatMessage(
            { id: 'v5.appointment.appointment_time' },
            startTime
          )}
        </div>
        <footer>
          <button
            className='btn btn-text appointment-cancelbtn'
            onClick={() => {
              abductionBack(props.onSuccess);
              props.onCancel();
            }}
          >
            {intl.formatMessage({ id: 'v5.appointment.select_new_time_text' })}
          </button>
          <button
            className='btn btn-text appointment-cancelbtn'
            onClick={() =>
              isPC
                ? cancel(intl, confirmConfig, isPC).then(props.onCancel)
                : setShowPopup(true)
            }
          >
            {intl.formatMessage({
              id: 'v5.appointment.cancel_appointment_text',
            })}
          </button>
        </footer>
      </section>
      <Popup
        show={showPopup}
        changeShow={() => setShowPopup(false)}
        className='cancel-appointment-confirm'
      >
        <main>{confirmConfig.content}</main>
        <footer>
          <button
            className='btn btn-primary-f'
            onClick={() => {
              setShowPopup(false);
              cancel(intl, {}, isPC).then(props.onCancel);
            }}
          >
            {confirmConfig.cancelBtnText}
          </button>
          <button
            className='btn btn-primary-o'
            onClick={() => setShowPopup(false)}
          >
            {confirmConfig.confirmBtnText}
          </button>
        </footer>
      </Popup>
    </>
  );
};

const Appointment = () => {
  const intl = useIntl();
  const { width } = useWindowSize();
  const isPC = width >= 980;
  const [appointment, setAppointment] = useState(isAppointment);
  useMount(() => {
    setTimeout(() => {
      mpaasTracker.View('Bespoke_BookPage', {
        bizType: 'Page',
      });
    }, 1000);

    // 判断预约成功与否
    const bespoke_appointment = localStorage.bespoke_appointment;
    if (bespoke_appointment) {
      formObj = JSON.parse(bespoke_appointment);
      // 辅助字段检测用
      formObj.contactTime = '';
      isAppointment = true;
      setAppointment(true);
      console.log(formObj, isAppointment);
    } else {
      isAppointment = false;
      setAppointment(false);
    }
  });
  const isAQMapp = envs.isAQMAPP;
  return (
    <div className='bg'>
      <Layout
        css={css`
          background: #e5e5e5;
        `}
        showHeader={!isAQMapp && !isPC}
        showFooter={!isAQMapp && !isPC}
      >
        <SEO
          title={intl.formatMessage({ id: 'professional-investors.title' })}
          description={intl.formatMessage({
            id: 'professional-investors.description',
          })}
          keywords={intl.formatMessage({
            id: 'professional-investors.keywords',
          })}
        />
        <div className='appointment'>
          <div className='appointment-content'>
            <header
              css={css`
                ${csss.mainPadding}
              `}
              className='text-center'
            >
              <img css={csss.logo} src={logo} />
            </header>
            <Judge>
              <Success
                if={appointment}
                isPC={isPC}
                onCancel={() => setAppointment(false)}
                onSuccess={() => setAppointment(true)}
              ></Success>
              <Form
                else
                isPC={isPC}
                onSuccess={() => setAppointment(true)}
              ></Form>
            </Judge>
          </div>
        </div>
      </Layout>
    </div>
  );
};

const csss = {
  mainPadding: css`
    padding-bottom: 20px;
    ${Tablet} {
      padding-bottom: 30px;
    }
    ${Desktop} {
      padding-bottom: 15px;
    }
  `,
  main: css`
    text-align: left;
    margin-left: auto;
    margin-right: auto;
    background: #fff;
    ${Tablet} {
      width: 100%;
      padding: 0 60px;
    }
    ${Desktop} {
      width: 100%;
      padding: 0 100px;
      border-top-right-radius: 15px;
      border-bottom-left-radius: 15px;
    }
  `,
  logo: css`
    width: 60%;
    ${Tablet} {
      width: 213px;
    }
    ${Desktop} {
      width: 250px;
    }
  `,
  title: css`
    font-size: 20px;
    text-align: center;
    width: 100%;
    ${Tablet} {
      font-size: 28px;
    }
    ${Desktop} {
      font-size: 28px;
    }
  `,
  explain: css`
    font-size: 14px;
    width: 100%;
    text-align: center;
  `,
  sub_title: css`
    font-size: 14px;
    padding-bottom: 20px;
    ${Tablet} {
      padding-bottom: 30px;
    }
    ${Desktop} {
      padding-bottom: 30px;
    }
  `,
  inputPadding: css``,
  btnPrimaryF: css`
    width: 100%;
    background: ${bespokeMainColor};
    border-color: ${bespokeMainColor};
    color: #fff;
    font-size: 14px;
    ${Tablet} {
      margin-bottom: 30px;
      font-size: 18px;
    }
    ${Desktop} {
      margin-bottom: 30px;
      font-size: 18px;
    }
  `,
  submitBtn: css`
    margin-top: 10px;
  `,
  inputFontSize: css`
    font-size: 12px;
    ${Tablet} {
      font-size: 14px;
    }
    ${Desktop} {
      font-size: 18px;
    }
  `,
  select: css`
    width: auto;
    padding: 0 40px 0 20px;
    border-right: 1px solid #ccc;
    border-radius: 0;
    background-position: calc(100% - 15px);
    height: auto;
    line-height: 1;
    &::-ms-expand {
      display: none;
    }
  `,
  currency: css`
    padding-right: 20px;
  `,
  error_text: css`
    font-size: 12px;
    padding: 0 20px;
    min-height: 20px;
    ${Tablet} {
      min-height: 30px;
    }
    ${Desktop} {
      min-height: 30px;
    }
  `,
};

export default Appointment;
